import * as React from "react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import type { ReactNode } from "react";
import {
  PublicClientApplication,
  type AccountInfo,
} from "@azure/msal-browser";
import { buildMsalConfig, SCOPES } from "./msalConfig";
import type { User } from "./types";
import { AUTH_REQUIRED_EVENT } from "../../api/http";

/* global Office */

const STORAGE_USER = "esi_flow_user";
const STORAGE_TOKEN = "esi_flow_access_token";
const STORAGE_SESSION = "esi_flow_session";

// Backend OAuth endpoints. The backend handles the AAD code exchange so
// the auth flow doesn't need to live inside an Office dialog (which Word
// on the web kills with error 12006 when MSAL navigates to AAD).
const BACKEND_BASE =
  (typeof process !== "undefined" && process.env && process.env.BACKEND_BASE) ||
  "https://api.esiflow.com";

type AuthCtx = {
  user: User | null;
  loading: boolean;
  error: string | null;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  getToken: () => string | null;
};

const Ctx = createContext<AuthCtx | null>(null);

function userFromAccount(account: AccountInfo): User {
  return {
    id: account.localAccountId,
    name: account.name || account.username,
    email: account.username,
    username: account.username,
    tenantId: account.tenantId,
  };
}

function isInsideOffice(): boolean {
  return (
    typeof Office !== "undefined" &&
    !!Office?.context &&
    !!Office.context.host
  );
}

function isOpenBrowserWindowAvailable(): boolean {
  return (
    isInsideOffice() &&
    typeof Office.context?.ui?.openBrowserWindow === "function"
  );
}

function generateState(): string {
  // 32-char URL-safe random string. Office API rejects very long states; the
  // backend regex caps at 128.
  const arr = new Uint8Array(24);
  crypto.getRandomValues(arr);
  return Array.from(arr, (b) => b.toString(16).padStart(2, "0")).join("");
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(() => {
    try {
      const raw = localStorage.getItem(STORAGE_USER);
      return raw ? (JSON.parse(raw) as User) : null;
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const msalRef = useRef<PublicClientApplication | null>(null);
  const pollAbortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    const requireSignIn = () => {
      localStorage.removeItem(STORAGE_SESSION);
      localStorage.removeItem(STORAGE_TOKEN);
      localStorage.removeItem(STORAGE_USER);
      setUser(null);
      setError("Your session has expired or is no longer valid. Please sign in with Microsoft again.");
    };
    window.addEventListener(AUTH_REQUIRED_EVENT, requireSignIn);
    return () => window.removeEventListener(AUTH_REQUIRED_EVENT, requireSignIn);
  }, []);

  const getMsal = useCallback(async () => {
    if (!msalRef.current) {
      const instance = new PublicClientApplication(buildMsalConfig());
      await instance.initialize();
      msalRef.current = instance;
    }
    return msalRef.current;
  }, []);

  // Browser path: process loginRedirect response on mount.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const msal = await getMsal();
        const result = await msal.handleRedirectPromise();
        if (cancelled || !result || !result.account) return;
        const u = userFromAccount(result.account);
        localStorage.setItem(STORAGE_USER, JSON.stringify(u));
        if (result.accessToken) {
          localStorage.setItem(STORAGE_TOKEN, result.accessToken);
        }
        setUser(u);
      } catch (err) {
        if (!cancelled) {
          const msg = err instanceof Error ? err.message : "Sign-in failed.";
          setError(msg);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getMsal]);

  // ── Office path ─ openBrowserWindow + backend polling ──────────────
  // The user's actual browser handles the AAD interaction (no Office
  // dialog window involved at all). Backend exchanges the auth code for
  // tokens and stashes the result keyed by `state`. The task pane polls
  // /api/auth/check until the result is ready.
  const loginViaBackendOAuth = useCallback(async (): Promise<User> => {
    const state = generateState();
    const startUrl = `${BACKEND_BASE}/api/auth/start?state=${encodeURIComponent(state)}`;

    if (isOpenBrowserWindowAvailable()) {
      // Office opens the URL in the user's default browser, outside Office.
      Office.context.ui.openBrowserWindow(startUrl);
    } else {
      // Standalone browser fallback (unusual — taskpane outside Office)
      window.open(startUrl, "_blank", "noopener,noreferrer");
    }

    // Cancel any prior outstanding poll
    pollAbortRef.current?.abort();
    const ac = new AbortController();
    pollAbortRef.current = ac;

    const checkUrl = `${BACKEND_BASE}/api/auth/check?state=${encodeURIComponent(state)}`;
    const deadline = Date.now() + 5 * 60 * 1000; // 5-minute auth window

    while (!ac.signal.aborted) {
      if (Date.now() > deadline) {
        throw new Error("Sign-in timed out. Please try again.");
      }
      await new Promise((r) => setTimeout(r, 2000));
      if (ac.signal.aborted) break;

      let resp: Response;
      try {
        resp = await fetch(checkUrl, { signal: ac.signal });
      } catch (e) {
        if ((e as Error).name === "AbortError") break;
        // Transient network — keep polling.
        continue;
      }

      if (resp.status === 404) {
        // State unknown (expired). Treat as failure.
        throw new Error("Sign-in session expired. Please try again.");
      }
      if (!resp.ok) continue;

      const data = (await resp.json().catch(() => null)) as
        | { status: "pending" }
        | {
            status: "success";
            user: User;
            accessToken?: string;
            idToken?: string;
            sessionToken?: string | null;
          }
        | { status: "error"; error: string }
        | null;

      if (!data) continue;
      if (data.status === "pending") continue;
      if (data.status === "error") throw new Error(data.error);

      if (data.accessToken) {
        localStorage.setItem(STORAGE_TOKEN, data.accessToken);
      }
      if (data.sessionToken) {
        localStorage.setItem(STORAGE_SESSION, data.sessionToken);
      }
      return data.user;
    }
    throw new Error("Sign-in cancelled.");
  }, []);

  const loginViaRedirect = useCallback(async (): Promise<never> => {
    const msal = await getMsal();
    await msal.loginRedirect({ scopes: SCOPES, prompt: "select_account" });
    return new Promise<never>(() => {
      /* navigation pending */
    });
  }, [getMsal]);

  const login = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      if (isInsideOffice()) {
        const u = await loginViaBackendOAuth();
        localStorage.setItem(STORAGE_USER, JSON.stringify(u));
        setUser(u);
      } else {
        await loginViaRedirect();
      }
    } catch (err: unknown) {
      const e = err as { errorCode?: string; message?: string };
      if (e?.errorCode === "user_cancelled") {
        setError(null);
      } else {
        setError(e?.message || "Sign-in failed.");
        // eslint-disable-next-line no-console
        console.error("Sign-in error:", err);
      }
      throw err;
    } finally {
      setLoading(false);
    }
  }, [loginViaBackendOAuth, loginViaRedirect]);

  const logout = useCallback(async () => {
    pollAbortRef.current?.abort();
    try {
      const msal = await getMsal();
      msal.clearCache();
    } catch {
      /* noop */
    }
    localStorage.removeItem(STORAGE_USER);
    localStorage.removeItem(STORAGE_TOKEN);
    localStorage.removeItem(STORAGE_SESSION);
    setUser(null);
    setError(null);
  }, [getMsal]);

  const getToken = useCallback(() => localStorage.getItem(STORAGE_TOKEN), []);

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_USER) {
        setUser(e.newValue ? (JSON.parse(e.newValue) as User) : null);
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  return (
    <Ctx.Provider value={{ user, loading, error, login, logout, getToken }}>
      {children}
    </Ctx.Provider>
  );
}

export function useAuth(): AuthCtx {
  const v = useContext(Ctx);
  if (!v) throw new Error("useAuth must be used within AuthProvider");
  return v;
}
